import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 20px;
`;

const CardTitle = styled.h2`
  color: #ff6b00;
  font-size: 1.5rem;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const Subtitle = styled.div`
  color: #888;
  font-size: 0.9rem;
  margin-bottom: 25px;
  line-height: 1.6;
`;

const DivergenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const DivergenceItem = styled.div`
  background: ${props =>
    props.alertType === 'OPPORTUNITY' ? 'rgba(0, 255, 0, 0.05)' :
    'rgba(255, 170, 0, 0.05)'
  };
  border-left: 4px solid ${props =>
    props.alertType === 'OPPORTUNITY' ? '#00ff00' : '#ffaa00'
  };
  padding: 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props =>
      props.alertType === 'OPPORTUNITY' ? 'rgba(0, 255, 0, 0.1)' :
      'rgba(255, 170, 0, 0.1)'
    };
    transform: translateX(5px);
  }
`;

const DivergenceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
  gap: 15px;
  flex-wrap: wrap;
`;

const Question = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  flex: 1;
  min-width: 200px;
`;

const AlertBadge = styled.div`
  background: ${props =>
    props.alertType === 'OPPORTUNITY' ? '#00ff00' : '#ffaa00'
  };
  color: #000;
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ProbabilityRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin: 12px 0;
`;

const ProbBox = styled.div`
  background: #0a0a0a;
  padding: 12px;
  border-radius: 4px;
`;

const ProbLabel = styled.div`
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const ProbValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${props => props.color || '#fff'};
`;

const DivergenceValue = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.positive ? '#00ff00' : '#ff6b00'};
  margin-top: 5px;
`;

const Reasoning = styled.div`
  color: #aaa;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #333;
`;

const CategoryTag = styled.span`
  background: #1a1a1a;
  color: #ff6b00;
  padding: 3px 10px;
  border-radius: 3px;
  font-size: 0.75rem;
  border: 1px solid #333;
  margin-right: 8px;
`;

const Source = styled.span`
  color: #666;
  font-size: 0.75rem;
`;

const LoadingMessage = styled.div`
  color: #888;
  text-align: center;
  padding: 40px;
  font-size: 1.1rem;
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const PredictionMarketDivergence = () => {
  const [divergences, setDivergences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDivergences = async () => {
      try {
        setLoading(true);

        const timestamp = Date.now();
        console.log('[PredictionMarketDivergence] Fetching from Polymarket API');
        const response = await fetch(`${API_BASE_URL}/api/polymarket?_t=${timestamp}`);

        if (!response.ok) {
          throw new Error('Failed to fetch prediction market data');
        }

        const data = await response.json();
        console.log('[PredictionMarketDivergence] Received', data.length, 'divergence alerts');

        setDivergences(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching prediction market divergences:', err);
        setLoading(false);
      }
    };

    fetchDivergences();

    // Refresh every hour (prediction markets change slower than news)
    const interval = setInterval(fetchDivergences, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const openMarket = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardTitle>🎯 Prediction Market Divergences</CardTitle>
        <LoadingMessage>Analyzing prediction markets...</LoadingMessage>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>🎯 Prediction Market Divergences: Trade Signals</CardTitle>
      <Subtitle>
        Markets where Polymarket probabilities diverge &gt;20% from your thesis assumptions.
        <strong> OPPORTUNITY</strong> = potential mispricing to exploit.
        <strong> RISK</strong> = market sees something you don't - investigate.
      </Subtitle>

      {divergences.length === 0 ? (
        <LoadingMessage>No significant divergences found. Markets align with thesis.</LoadingMessage>
      ) : (
        <DivergenceList>
          {divergences.map((div, idx) => (
            <DivergenceItem
              key={div.id || idx}
              alertType={div.alertType}
              onClick={() => openMarket(div.url)}
            >
              <DivergenceHeader>
                <Question>{div.question}</Question>
                <AlertBadge alertType={div.alertType}>{div.alertType}</AlertBadge>
              </DivergenceHeader>

              <div style={{ marginBottom: '12px' }}>
                <CategoryTag>{div.category}</CategoryTag>
                <Source>Source: {div.source}</Source>
              </div>

              <ProbabilityRow>
                <ProbBox>
                  <ProbLabel>Market Probability</ProbLabel>
                  <ProbValue color="#00aaff">
                    {(div.marketProb * 100).toFixed(0)}%
                  </ProbValue>
                </ProbBox>

                <ProbBox>
                  <ProbLabel>Your Thesis</ProbLabel>
                  <ProbValue color="#ff6b00">
                    {(div.thesisProb * 100).toFixed(0)}%
                  </ProbValue>
                </ProbBox>

                <ProbBox>
                  <ProbLabel>Divergence</ProbLabel>
                  <ProbValue>
                    {(Math.abs(div.divergence) * 100).toFixed(0)}% gap
                  </ProbValue>
                  <DivergenceValue positive={div.alertType === 'OPPORTUNITY'}>
                    {div.divergence > 0 ? '+' : ''}{(div.divergence * 100).toFixed(1)}%
                  </DivergenceValue>
                </ProbBox>
              </ProbabilityRow>

              <Reasoning>
                <strong>Analysis:</strong> {div.reasoning}
              </Reasoning>
            </DivergenceItem>
          ))}
        </DivergenceList>
      )}
    </Card>
  );
};

export default PredictionMarketDivergence;
